import { useEffect, useRef, useState } from 'react'

/** Bevorzugte Formate: Chrome/Firefox liefern WebM/Opus, Safari MP4/AAC. Beide nimmt die Transkriptions-API an. */
const FORMATE = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']

export function aufnahmeFormat(unterstuetzt: (typ: string) => boolean): string | undefined {
  return FORMATE.find(unterstuetzt)
}

/** 30 Minuten; bei 32 kbit/s rund 7 MB und damit sicher unter dem 25-MB-Limit der API */
export const MAX_DAUER_MS = 30 * 60 * 1000

export interface FertigeAufnahme {
  audio: Blob
  typ: string
  beginn: Date
  dauerMs: number
}

type Zustand = { art: 'bereit' } | { art: 'laeuft'; beginn: number } | { art: 'fehler'; meldung: string }

/**
 * Kapselt MediaRecorder, Pegelanzeige und Wake-Lock (damit das Display waehrend der Aufnahme nicht ausgeht).
 * [onFertig] bekommt die fertige Aufnahme, sobald [stoppen] aufgerufen wurde oder [maxDauerMs] erreicht ist.
 */
export function useAufnahme(onFertig: (a: FertigeAufnahme) => void, maxDauerMs = MAX_DAUER_MS) {
  const [zustand, setZustand] = useState<Zustand>({ art: 'bereit' })
  const [pegel, setPegel] = useState(0)
  const [jetzt, setJetzt] = useState(Date.now())
  const recorder = useRef<MediaRecorder | null>(null)
  const verwerfen = useRef(false)
  const aufraeumen = useRef<() => void>(() => {})
  const onFertigRef = useRef(onFertig)
  onFertigRef.current = onFertig

  useEffect(() => () => aufraeumen.current(), [])

  useEffect(() => {
    if (zustand.art !== 'laeuft') return
    const t = setInterval(() => {
      setJetzt(Date.now())
      if (Date.now() - zustand.beginn >= maxDauerMs) recorder.current?.stop()
    }, 250)
    return () => clearInterval(t)
  }, [zustand, maxDauerMs])

  const starten = async () => {
    if (zustand.art === 'laeuft') return
    const typ = aufnahmeFormat((t) => MediaRecorder.isTypeSupported(t))
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    } catch (e) {
      setZustand({ art: 'fehler', meldung: mikrofonFehler(e) })
      return
    }
    const rec = new MediaRecorder(stream, { mimeType: typ, audioBitsPerSecond: 32000 })
    const teile: Blob[] = []
    const beginn = Date.now()
    verwerfen.current = false

    // Pegelanzeige, damit man sieht, dass das Mikrofon etwas hoert
    const kontext = new AudioContext()
    const analyse = kontext.createAnalyser()
    analyse.fftSize = 512
    kontext.createMediaStreamSource(stream).connect(analyse)
    const werte = new Uint8Array(analyse.fftSize)
    let rahmen = 0
    const messen = () => {
      analyse.getByteTimeDomainData(werte)
      let summe = 0
      for (const w of werte) summe += ((w - 128) / 128) ** 2
      setPegel(Math.min(1, Math.sqrt(summe / werte.length) * 4))
      rahmen = requestAnimationFrame(messen)
    }
    messen()

    let wachhalten: WakeLockSentinel | undefined
    navigator.wakeLock?.request('screen').then((w) => (wachhalten = w)).catch(() => {})

    aufraeumen.current = () => {
      cancelAnimationFrame(rahmen)
      stream.getTracks().forEach((t) => t.stop())
      kontext.close().catch(() => {})
      wachhalten?.release().catch(() => {})
      aufraeumen.current = () => {}
    }

    rec.ondataavailable = (e) => e.data.size > 0 && teile.push(e.data)
    rec.onstop = () => {
      aufraeumen.current()
      setPegel(0)
      setZustand({ art: 'bereit' })
      if (verwerfen.current || teile.length === 0) return
      const audioTyp = rec.mimeType || typ || 'audio/webm'
      onFertigRef.current({ audio: new Blob(teile, { type: audioTyp }), typ: audioTyp, beginn: new Date(beginn), dauerMs: Date.now() - beginn })
    }
    // Zeitscheiben, damit bei einem Absturz nicht alles in einem einzigen Puffer haengt
    rec.start(1000)
    recorder.current = rec
    setJetzt(beginn)
    setZustand({ art: 'laeuft', beginn })
  }

  const stoppen = () => recorder.current?.state === 'recording' && recorder.current.stop()

  const abbrechen = () => {
    verwerfen.current = true
    stoppen()
  }

  return {
    zustand,
    pegel,
    dauerMs: zustand.art === 'laeuft' ? jetzt - zustand.beginn : 0,
    starten,
    stoppen,
    abbrechen,
    fehlerQuittieren: () => setZustand({ art: 'bereit' }),
  }
}

function mikrofonFehler(e: unknown): string {
  const name = (e as DOMException)?.name
  if (name === 'NotAllowedError') return 'Kein Zugriff auf das Mikrofon. Bitte in den Browser-Einstellungen erlauben.'
  if (name === 'NotFoundError') return 'Kein Mikrofon gefunden.'
  if (!window.isSecureContext) return 'Das Mikrofon ist nur über HTTPS verfügbar.'
  return `Mikrofon nicht verfügbar (${name ?? e})`
}
